import { motion } from "framer-motion";
import { Sparkles, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HOLIDAY_PACKAGES, COMPANION_PACKAGES } from "../../data/holidays";
import { Chapter, Reveal, EnquiryButton, EASE } from "./shared";
import { useEarlyBird } from "../../lib/useEarlyBird";

// Limited launch promotion: ONE global allocation of 4 direct bookings for the whole 2027 season.
export default function HolidayEarlyBird() {
  const { t } = useTranslation();
  const rules = t("holidays.early_bird.rules", { returnObjects: true });
  const eb = useEarlyBird();
  if (!eb.active) {
    return (
      <section id="early-bird" className="bg-bone-deep py-16 lg:py-20 scroll-mt-20 border-y border-forest/10" data-testid="holidays-early-bird-closed">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Reveal className="bg-bone border border-bone-deep p-7 sm:p-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
            <span className="inline-flex self-start items-center gap-2 bg-moss text-white text-[10px] uppercase tracking-wider-3 px-3 py-1.5">{t("holidays.early_bird.closed_badge")}</span>
            <div>
              <h2 className="font-display text-3xl sm:text-4xl text-forest leading-tight">{t("holidays.early_bird.closed_title")}</h2>
              <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">{t("holidays.early_bird.closed_text")}</p>
            </div>
          </Reveal>
        </div>
      </section>
    );
  }
  return (
    <section id="early-bird" className="bg-bone-deep py-20 lg:py-28 scroll-mt-20 border-y border-forest/10" data-testid="holidays-early-bird">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="relative bg-bone border border-terracotta/40">
          <div className="absolute -top-3 left-6 lg:left-10 inline-flex items-center gap-2 bg-terracotta text-white text-[10px] uppercase tracking-wider-3 px-3 py-1.5" data-testid="early-bird-badge">
            <Sparkles className="w-3 h-3" /> {t("holidays.early_bird.eyebrow")}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 p-7 sm:p-10 lg:p-14">
            <div className="lg:col-span-5">
              <Chapter number={t("holidays.early_bird.chapter")} label={t("holidays.early_bird.chapter_label")} />
              <Reveal delay={0.1}>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-forest leading-[1.0] mt-6 uppercase tracking-tight" data-testid="early-bird-title">
                  {t("holidays.early_bird.title_a")}
                  <span className="block italic normal-case text-terracotta mt-2 text-2xl sm:text-3xl lg:text-4xl">{t("holidays.early_bird.title_b")}</span>
                </h2>
              </Reveal>
              <Reveal delay={0.2} className="mt-6 text-base md:text-lg text-forest leading-relaxed">{t("holidays.early_bird.intro")}</Reveal>
              {eb.loaded && (
                <Reveal delay={0.22} className="mt-5 inline-flex items-center gap-2 border border-terracotta text-terracotta text-[10px] uppercase tracking-wider-3 px-3 py-2" data-testid="early-bird-remaining">
                  <span className="w-1.5 h-1.5 rounded-full bg-terracotta animate-pulse" />
                  {eb.remaining === 1 ? t("holidays.early_bird.remaining_one") : t("holidays.early_bird.remaining", { count: eb.remaining, total: eb.total })}
                </Reveal>
              )}

              <Reveal delay={0.25} className="mt-8">
                <div className="text-[10px] uppercase tracking-wider-3 text-moss mb-3">{t("holidays.early_bird.rules_title")}</div>
                <ul className="space-y-3">
                  {rules.map((r, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground leading-relaxed" data-testid={`early-bird-rule-${i}`}>
                      <Check className="w-4 h-4 text-terracotta shrink-0 mt-0.5" /> <span>{r}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-5 border-l-2 border-terracotta pl-4 text-sm text-forest leading-relaxed" data-testid="early-bird-example">
                  <span className="text-[10px] uppercase tracking-wider-3 text-moss block mb-1">{t("holidays.early_bird.example_label")}</span>
                  {t("holidays.early_bird.example")}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7 flex flex-col gap-6">
              <PriceTable title={t("holidays.early_bird.riders")} rows={HOLIDAY_PACKAGES} itemKey="items" testid="early-bird-riders" />
              <PriceTable title={t("holidays.early_bird.companions")} rows={COMPANION_PACKAGES} itemKey="companion_items" testid="early-bird-companions" delay={0.1} />
              <Reveal delay={0.2} className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 pt-2">
                <EnquiryButton variant="terracotta" testid="early-bird-cta" pkg="early-bird">{t("holidays.early_bird.cta")}</EnquiryButton>
                <p className="text-xs text-muted-foreground leading-relaxed" data-testid="early-bird-confirm">{t("holidays.early_bird.confirm")}</p>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function PriceTable({ title, rows, itemKey, testid, delay = 0 }) {
  const { t } = useTranslation();
  return (
    <motion.div
      data-testid={testid}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "0px 0px -40px 0px" }}
      transition={{ duration: 0.8, ease: EASE, delay }}
      className="bg-white border border-bone-deep"
    >
      <div className="flex items-center justify-between px-5 py-3 border-b border-bone-deep">
        <span className="text-[10px] uppercase tracking-wider-3 text-forest">{title}</span>
        <span className="text-[10px] uppercase tracking-wider-3 text-moss">{t("holidays.early_bird.per_person")}</span>
      </div>
      <div className="divide-y divide-bone-deep">
        {rows.map((r) => (
          <div key={r.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-4 px-5 py-4" data-testid={`${testid}-${r.key}`}>
            <span className="font-display text-xl sm:text-2xl text-forest leading-tight">{t(`holidays.packages.items.${r.key}.duration`)}</span>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider-2 text-terracotta">{t("holidays.early_bird.eb")}</div>
              <div className="font-display text-3xl sm:text-4xl text-terracotta leading-none mt-0.5">{t(`holidays.early_bird.${itemKey}.${r.key}.eb`)}</div>
            </div>
            <div className="text-right min-w-[84px]">
              <div className="text-[10px] uppercase tracking-wider-2 text-moss">{t("holidays.early_bird.regular")}</div>
              <div className="font-display text-xl sm:text-2xl text-muted-foreground leading-none mt-1">{t(`holidays.early_bird.${itemKey}.${r.key}.regular`)}</div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
